import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import classNames from 'classnames';
import MomentUtils from '@date-io/moment';
import moment from 'moment';

import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker
} from '@material-ui/pickers';

import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';

import { useStyles } from 'components/time-picker/styles';

const CustomTimePicker = ({
  input,
  meta: { touched, error, submitFailed },
  label,
  isHiddenErrorMsg,
  className,
  selectedDate,
  startTimeValue,
  availableUnitsData
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const now = moment();
  const isToday = selectedDate && selectedDate.isSame(now, 'day');
  const reliefNow = now.clone().subtract(1, 'minutes');

  const normalizePickerValue = (value, selectedDate) => {
    if (!value) return null;

    let normalizedValue;

    if (moment.isMoment(value)) {
      normalizedValue = value;
    } else if (typeof value === 'string' || value instanceof Date) {
      normalizedValue = moment(value);
    } else {
      return null;
    }

    if (
      selectedDate &&
      moment.isMoment(selectedDate) &&
      normalizedValue.isValid()
    ) {
      const combinedDateTime = selectedDate
        .clone()
        .hour(normalizedValue.hour())
        .minute(normalizedValue.minute())
        .second(0)
        .millisecond(0);
      return combinedDateTime;
    }

    return normalizedValue.isValid() ? normalizedValue : null;
  };

  const pickerValue = normalizePickerValue(input.value, selectedDate);

  const onChangeHandler = date => {
    if (date && moment.isMoment(date)) {
      let combinedDateTime = date;

      if (selectedDate && moment.isMoment(selectedDate)) {
        combinedDateTime = selectedDate
          .clone()
          .hour(date.hour())
          .minute(date.minute())
          .second(0)
          .millisecond(0);
      }

      if (isToday && combinedDateTime.isBefore(reliefNow, 'minute')) {
        enqueueSnackbar(
          'Cannot select past times for today. Please choose a future time.',
          {
            variant: 'warning',
            autoHideDuration: 4000
          }
        );
        return;
      }

      if (availableUnitsData && Array.isArray(availableUnitsData)) {
        const selectedDateStr = selectedDate?.format('YYYY-MM-DD');
        let isExplicitlyUnavailable = false;

        if (selectedDateStr) {
          availableUnitsData.forEach(rentalItem => {
            if (rentalItem.availableUnitsPerDate) {
              rentalItem.availableUnitsPerDate.forEach(slot => {
                if (slot.date === selectedDateStr) {
                  const slotStart = moment.utc(slot.startTime);
                  const slotEnd = moment.utc(slot.endTime);
                  const timeToCheckUTC = combinedDateTime.clone().utc();

                  if (
                    timeToCheckUTC.isSameOrAfter(slotStart) &&
                    timeToCheckUTC.isSameOrBefore(slotEnd) &&
                    slot.availableUnits === 0
                  ) {
                    isExplicitlyUnavailable = true;
                  }
                }
              });
            }
          });
        }

        if (isExplicitlyUnavailable) {
          enqueueSnackbar(
            'This time slot is fully booked. Please choose a different time.',
            {
              variant: 'error',
              autoHideDuration: 4000
            }
          );
          return;
        }
      }

      if (input.name === 'endTime' && startTimeValue) {
        const startMoment = moment.isMoment(startTimeValue)
          ? startTimeValue
          : moment(startTimeValue);

        if (combinedDateTime.isSame(startMoment)) {
          enqueueSnackbar('Start time and end time cannot be the same.', {
            variant: 'error',
            autoHideDuration: 4000
          });
          return;
        }

        if (combinedDateTime.isBefore(startMoment)) {
          enqueueSnackbar('End time cannot be before start time.', {
            variant: 'error',
            autoHideDuration: 4000
          });
          return;
        }
      }

      input.onChange(combinedDateTime);
    } else {
      input.onChange(null);
    }
  };

  return (
    <div className={classNames(classes.root, className)}>
      <InputLabel
        className={classes.label}
        htmlFor={`${label}-input`}
        classes={{ disabled: classes.disabledLabel }}
      >
        {t(label)}
      </InputLabel>

      <div
        className={classNames('custom-time-picker', classes.customTimePicker, {
          [classes.error]: (submitFailed || touched) && error,
          'past-time':
            isToday && pickerValue && pickerValue.isBefore(reliefNow, 'minute')
        })}
      >
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardTimePicker
            clearable={false}
            ampm={false}
            label={label}
            value={pickerValue}
            onChange={onChangeHandler}
          />
        </MuiPickersUtilsProvider>
      </div>

      {!isHiddenErrorMsg && (touched || submitFailed) && error && (
        <FormHelperText error>{error}</FormHelperText>
      )}
    </div>
  );
};

export default CustomTimePicker;